import type { CSSProperties } from 'react';
import { Box, Text } from '@mantine/core';

import type { ReportStatus, ReportTypeIdentifier } from '../../types/reports';
import { statusPalette, vdzGray } from '../../theme/colors';
import { useFieldLabelMap } from '../form/fieldTabRegistry';
import {
  groupChangedFields,
  revisionNoticeState,
  tabConfigFor,
} from './changedFieldGroups';
import classes from './RevisionNotice.module.css';

interface RevisionNoticeProps {
  reportType: ReportTypeIdentifier;
  status: ReportStatus;
  reason: string | null | undefined;
  changedFields: string[] | null | undefined;
  isReviewer: boolean;
}

/**
 * Hinweis ueber der Reiterleiste (Phase 43, D-09, Sketch 026 Runde 2): Begruendung der
 * Rueckgabe, fuer den Pruefer nach dem Neueinreichen zusaetzlich die geaenderten Felder (D-13).
 * Bewusst ohne Datum, Absender und Einklappen (D-04). Die Begruendung ist Freitext eines
 * anderen Benutzers und wird ausschliesslich als Textknoten gerendert (T-43-24).
 */
export function RevisionNotice({
  reportType,
  status,
  reason,
  changedFields,
  isReviewer,
}: RevisionNoticeProps) {
  const labels = useFieldLabelMap();
  const state = revisionNoticeState(status, reason, changedFields, isReviewer);
  if (!state) return null;

  const returned = state.variant === 'returned';
  const palette = statusPalette.revision;
  const style = {
    '--notice-border': returned ? palette.border : vdzGray[3],
    '--notice-stripe': returned ? palette.fg : vdzGray[6],
    '--notice-title': returned ? palette.fg : vdzGray[8],
  } as CSSProperties;

  const tabs = tabConfigFor(reportType);
  const { groups, unlabeledCount } = groupChangedFields(
    changedFields ?? [],
    labels,
    tabs?.order ?? null,
    tabs?.titles ?? null
  );
  const unchanged = (changedFields ?? []).length === 0;

  return (
    <Box className={classes.notice} style={style} role="status">
      <div className={classes.inner}>
        <Text component="h2" className={classes.title}>
          Zur Überarbeitung zurückgegeben
        </Text>
        <Text component="p" className={classes.reason}>
          {reason}
        </Text>

        {state.showChanges && (
          <div className={classes.changes}>
            {unchanged ? (
              <p className={classes.none}>
                Das Werk hat nichts geändert und unverändert neu eingereicht.
              </p>
            ) : (
              <>
                <h3 className={classes.changesTitle}>Folgende Felder wurden geändert</h3>
                <ul className={classes.list}>
                  {groups.map((group) => (
                    <li key={group.title ?? '__flat'}>
                      {group.title !== null && <b>{group.title}: </b>}
                      {group.labels.join(', ')}
                    </li>
                  ))}
                  {unlabeledCount > 0 && <li>weitere Felder</li>}
                </ul>
              </>
            )}
          </div>
        )}
      </div>
    </Box>
  );
}

